import {
  ContentCardMap,
  ContentMain,
  ContentRight,
  LeftPane,
} from './Courses.styles'
import React, { useState, useEffect, useRef } from 'react'
import {
  clubDisplayFilter,
  filterBySearch,
  filterByStatus,
  filterByVisibleClubs,
} from '../domain/searchFilters'
import { useClubData } from '../hooks'

import { ContentCard } from '@app/evaluation/components/Preparation.styles'
import CourseMap from '@app/course-map'
import { Flex } from '@app/ui/Flex'
import { Text, View } from 'react-native'
import { Header } from './Header'
import TableData from '@app/components/molecules/TableData'
import RefreshCourses from '@app/components/molecules/TableData/RefreshCourses'
import useInvite from '@common/hooks/useInvite'
import { useMediaQuery } from 'react-responsive'
import { BranchStatus } from '@app/evaluation-core/types'
import useUser from '@common/hooks/useUser'
import { useSnackbar } from '@app/snackbar'
import theme from '@app/ui/theme'
import { useIsFocused } from '@react-navigation/native'
import ScreenWrapper from '@app/components/atoms/ScreenWrapper'
import { Spinner } from '@app/evaluation/components/Spinner'
import { LRCourse } from '../../types/Clubs'
import { MobileHeader } from './MobileHeader'

export type CourseWithStatus = LRCourse & { branchStatus: BranchStatus }

// The request only starts the import, the clubs and courses reach us over the
// Firestore listeners a while later. Give up waiting after this so a refresh
// that writes nothing cannot leave the page spinning.
const REFRESH_TIMEOUT = 60000

const Courses = () => {
  const [openSnackbar] = useSnackbar({
    style: {
      backgroundColor: theme.colors.warning,
    },
  })
  useInvite()
  const [searchValue, setSearchValue] = useState('')
  const [filterValue, setFilterValue] = useState(-1)
  const [regionFilter, setRegionFilter] = useState('')
  const [districtFilter, setDistrictFilter] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const {
    courses,
    clubs,
    courseDocs,
    clubDocs,
    loadingClubs,
    clubsWithCourses,
  } = useClubData()
  const docsAtRefresh = useRef({ courseDocs, clubDocs })

  const onRefreshingChange = (value: boolean) => {
    if (value) docsAtRefresh.current = { courseDocs, clubDocs }
    setRefreshing(value)
  }

  // Clubs and courses land in separate listener responses, so wait for both
  // before leaving the spinner - otherwise the empty state flashes by.
  useEffect(() => {
    if (!refreshing) return
    const arrived =
      courseDocs !== docsAtRefresh.current.courseDocs ||
      clubDocs !== docsAtRefresh.current.clubDocs
    if (arrived && courseDocs && clubDocs) {
      setRefreshing(false)
      return
    }
    const timeout = setTimeout(() => setRefreshing(false), REFRESH_TIMEOUT)
    return () => clearTimeout(timeout)
  }, [refreshing, courseDocs, clubDocs])

  const user = useUser()
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1524px)' })
  const isPhone = window.innerWidth <= 767
  const focused = useIsFocused()
  useEffect(() => {
    if (!user.golfID && focused) {
      // TODO: Check if this is needed
      // openSnackbar(
      //   <div onClick={() => navigate('Teams')}>
      //     Press here to go to your profile and add your GIT-ID.
      //   </div>,
      //   10000
      // )
    }
  }, [user.golfID, focused])

  if (loadingClubs || refreshing) {
    return (
      <ScreenWrapper style={{ display: 'flex', flex: 1 }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Spinner />
        </div>
      </ScreenWrapper>
    )
  }

  if (!clubs || !courses) {
    return (
      <ScreenWrapper style={{ display: 'flex', flex: 1 }}>
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontSize: 16,
              color: theme.colors.dark1,
              marginBottom: 16,
            }}
          >
            No courses found
          </Text>
          <RefreshCourses onRefreshingChange={onRefreshingChange} />
        </View>
      </ScreenWrapper>
    )
  }

  const visibleClubs = clubsWithCourses.filter((club, index) =>
    clubDisplayFilter(
      club,
      index,
      searchValue,
      filterValue,
      regionFilter,
      districtFilter
    )
  )

  const coursesWithClubLocationAndStatus = courses?.map((course) => {
    const club = clubs.find((club) => club.id === course.golfClubID)
    if (club) {
      return {
        ...course,
        branchStatus: club.branchStatus,
        mapLatitude: club.mapLatitude || club.latitude || '0',
        mapLongitude: club.mapLongitude || club.longitude || '0',
      }
    }
    return course
  })

  if (!courses) return null

  return (
    <ScreenWrapper style={{ display: 'flex', flex: 1 }}>
      <LeftPane>
        {!isPhone ? (
          <Header
            tablet={isTabletOrMobile}
            onChangeSearchValue={setSearchValue}
            searchValue={searchValue}
            onChangeFilterValue={(value) => setFilterValue(parseInt(value))}
            filterStatusValue={filterValue}
            onChangeRegionFilterValue={(value) => setRegionFilter(value)}
            regionFilterStatusValue={regionFilter}
            onChangeDistrictFilterValue={(value) => setDistrictFilter(value)}
            districtFilterStatusValue={districtFilter}
          />
        ) : (
          <MobileHeader
            tablet={isTabletOrMobile}
            onChangeSearchValue={setSearchValue}
            searchValue={searchValue}
            onChangeFilterValue={(value) => setFilterValue(parseInt(value))}
            filterStatusValue={filterValue}
            onChangeRegionFilterValue={(value) => setRegionFilter(value)}
            regionFilterStatusValue={regionFilter}
            onChangeDistrictFilterValue={(value) => setDistrictFilter(value)}
            districtFilterStatusValue={districtFilter}
          />
        )}
        <Flex row>
          <ContentMain>
            {courses && (
              <ContentCard>
                <TableData
                  clubs={clubsWithCourses}
                  searchValue={searchValue}
                  filterStatusValue={filterValue}
                  regionFilterStatusValue={regionFilter}
                  districtFilterStatusValue={districtFilter}
                  loading={loadingClubs}
                  onRefreshingChange={onRefreshingChange}
                />
              </ContentCard>
            )}
          </ContentMain>
          <ContentRight>
            <ContentCardMap>
              <CourseMap
                courses={coursesWithClubLocationAndStatus
                  .filter((course) => filterBySearch(course, null, searchValue))
                  .filter((course) =>
                    filterByStatus(course, course.branchStatus, filterValue)
                  )
                  .filter((course) =>
                    filterByVisibleClubs(course, visibleClubs)
                  )}
              />
            </ContentCardMap>
          </ContentRight>
        </Flex>
      </LeftPane>
    </ScreenWrapper>
  )
}

export default Courses
